import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferSource } from '@cds';
import { TotalsPrefix } from '@cds/betting-offer';
import { EventModel, EventOption, EventOptionGroup, VerticalDesignOptions } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { IntersectionObserverService } from '@frontend/sports/common/core/utils/dom';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { Promise as UnpatchedPromise } from '@rx-angular/cdk/zone-less/browser';
import { distinctUntilChanged, map, of, switchMap } from 'rxjs';

import { SourceOfPick } from '../../../betslip-base/models/pick-models';
import { EpcotConfigService } from '../../../common/epcot-config.service';
import { EventPickComponent } from '../../../option-pick/event-pick.component';
import { PopupManager } from '../../../popup/popup-manager.service';

@Component({
    selector: 'ms-option',
    templateUrl: 'option.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EventPickComponent, IconCustomComponent, NativePriceFormatterPipe, FontResizerComponent],
})
export class OptionComponent implements AfterViewInit, OnChanges {
    @Input() option: EventOption;
    @Input() optionGroup: EventOptionGroup;
    @Input() event: EventModel;
    @Input() tracking: TrackingSource;
    @Input() decorateAttribute?: boolean;
    @Input() isHighlighted = false;
    @Input() sourceOfPick: SourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() hideAttributeValue = false;
    @HostBinding('class') className = 'grid-option';
    @Input() showOptionname = false;
    @Input() verticalDesignOptions?: VerticalDesignOptions;
    @Input() showCenterOptionName = false;

    private detached = false;

    private ngZone = inject(NgZone);

    attributeValue?: string;
    showAttributeValue: boolean;
    showSuspendedSelection = this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled;
    showNoGoalText: boolean;
    readonly isEpcotEnabled: boolean;

    constructor(
        private sitecore: Sitecore,
        private popupManager: PopupManager,
        private eventDetailsConfig: EventDetailsConfig,
        private elementRef: ElementRef<HTMLElement>,
        private changeRef: ChangeDetectorRef,
        private destroyRef: DestroyRef,
        private strategy: RxStrategyProvider,
        private intersectionObserverService: IntersectionObserverService,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    ngAfterViewInit() {
        // defer subscription to intersectionObserver, as `OptionComponent`
        // isn't attached to the DOM yet
        UnpatchedPromise.resolve().then(() => this.observeIntersection());
    }

    @HostListener('click', ['$event'])
    clicked(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.popupManager.isPopupOpen('sports')) {
            this.popupManager.close();
        }
    }

    private observeIntersection() {
        this.intersectionObserverService
            .observe(this.elementRef)
            .pipe(
                map((entry) => entry.isIntersecting),
                distinctUntilChanged(),
                switchMap((inView) => {
                    if (!inView && !this.detached) {
                        this.changeRef.detach();
                        this.detached = true;

                        return of(true);
                    }
                    if (inView && this.detached) {
                        this.changeRef.reattach();
                        this.detached = false;

                        return this.strategy.schedule(() => this.changeRef.detectChanges(), {
                            scope: this,
                            patchZone: this.ngZone,
                        });
                    }

                    return of(true);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    get offline(): boolean {
        return this.showSuspendedSelection ? !this.optionGroup.online || !this.option.online : !this.option.online;
    }

    ngOnChanges(): void {
        this.setValue();
    }

    private setValue(): void {
        if (this.decorateAttribute && this.optionGroup.categoryAttribute && this.option.totalsPrefix) {
            const prefix =
                this.option.totalsPrefix === TotalsPrefix.Over
                    ? this.sitecore.eventGrid.SixPack_TotalsOver
                    : this.sitecore.eventGrid.SixPack_TotalsUnder;

            this.attributeValue = `${prefix} ${this.optionGroup.categoryAttribute}`;
        } else {
            this.attributeValue =
                this.optionGroup.source === OfferSource.V1
                    ? this.optionGroup.categoryAttribute || this.option.optionAttribute
                    : this.option.optionAttribute || this.optionGroup.categoryAttribute;
        }

        this.showAttributeValue = !this.hideAttributeValue && !!this.optionGroup.balanced && !!this.attributeValue;

        if (this.attributeValue && this.verticalDesignOptions?.showOptionAttributeDecorator) {
            const optionName = this.option.name.split(' ')[0];
            if (optionName === TotalsPrefix.Over || optionName === TotalsPrefix.Under) {
                const prefix =
                    optionName === TotalsPrefix.Over ? this.sitecore.eventGrid.SixPack_TotalsOver : this.sitecore.eventGrid.SixPack_TotalsUnder;
                this.attributeValue = `${prefix} ${this.attributeValue}`;
            }
        }

        if (!this.showAttributeValue && !this.hideAttributeValue) {
            this.showAttributeValue = !!this.optionGroup.description;
            this.attributeValue = this.optionGroup.description;
        }
        if (this.showOptionname && this.attributeValue && !this.optionGroup.categoryAttribute) {
            this.option.name = this.option.name.replace(this.attributeValue, '').trimEnd();
        }
        if (this.verticalDesignOptions?.showOptionName) {
            this.attributeValue = this.option.name;
            this.showAttributeValue = true;
        }
    }
}
